<script>
  import { onMount } from 'svelte';
  import gsap from 'gsap';
  import { ScrollTrigger } from 'gsap/ScrollTrigger';
  import Header from './components/Header.svelte';
  import ScrollSection from './components/ScrollSection.svelte';
  import VideoAnimation from './components/VideoAnimation.svelte';
  
  // Register ScrollTrigger with gsap
  gsap.registerPlugin(ScrollTrigger);
  
  let backgroundContainer;
  let currentBackground = 'blue';
  let flourishEmbedContainer;
  let policyScrollContainer;
  let scrollIndicators;
  let strengthsScrollContainer;
  let strengthsScrollIndicators;
  let weaknessesScrollContainer;
  let weaknessesScrollIndicators;
  let introVideoFinished = false;
  let introVideoElement;
  
  // Mobile dropdown selections
  let selectedPolicyCountry = 'Indonesia';
  let selectedStrengthsCountry = 'Indonesia';
  let selectedWeaknessesCountry = 'Indonesia';
  
  // Menu open states
  let policyMenuOpen = false;
  let strengthsMenuOpen = false;
  let weaknessesMenuOpen = false;
  
  const countries = ['Indonesia', 'Philippines', 'Malaysia', 'Singapore', 'Thailand', 'Vietnam'];
  
  function togglePolicyMenu() {
    policyMenuOpen = !policyMenuOpen;
    strengthsMenuOpen = false;
    weaknessesMenuOpen = false;
  }
  
  function toggleStrengthsMenu() {
    strengthsMenuOpen = !strengthsMenuOpen;
    policyMenuOpen = false;
    weaknessesMenuOpen = false;
  }
  
  function toggleWeaknessesMenu() {
    weaknessesMenuOpen = !weaknessesMenuOpen;
    policyMenuOpen = false;
    strengthsMenuOpen = false;
  }
  
  function selectPolicyCountry(country) {
    selectedPolicyCountry = country;
    policyMenuOpen = false;
  }
  
  function selectStrengthsCountry(country) {
    selectedStrengthsCountry = country;
    strengthsMenuOpen = false;
  }
  
  function selectWeaknessesCountry(country) {
    selectedWeaknessesCountry = country;
    weaknessesMenuOpen = false;
  }
  
  // Close menus when clicking outside
  function handleClickOutside(event) {
    if (!event.target.closest('.country-selector')) {
      policyMenuOpen = false;
      strengthsMenuOpen = false;
      weaknessesMenuOpen = false;
    }
  }
  
  // Background colors for different sections
  const backgroundColors = [
    'blue',    // Group 1
    'orange',  // Group 2 (formerly Group 3)
    'purple',  // Group 3 (formerly Group 4)
    'dark'     // Group 4 (formerly Group 5)
  ];
  
  // Load Flourish embed script dynamically
  function loadFlourishEmbed() {
    // Check if script is already loaded
    if (document.querySelector('script[src="https://public.flourish.studio/resources/embed.js"]')) {
      return;
    }
    
    const script = document.createElement('script');
    script.src = 'https://public.flourish.studio/resources/embed.js';
    script.async = true;
    document.head.appendChild(script);
  }
  
  onMount(() => {
    
    // Force scroll to top on page load/refresh
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });
    
    // Prevent scroll during intro video
    document.body.style.overflow = 'hidden';
    
    // Wait for video element to be ready, then play
    const tryPlayIntro = () => {
      if (introVideoElement) {
        introVideoElement.play().catch(err => {
          console.warn('Autoplay prevented:', err);
          // If autoplay fails, show the sequence anyway
          handleIntroVideoEnd();
        });
      } else {
        // Retry after a short delay if element isn't ready yet
        setTimeout(tryPlayIntro, 100);
      }
    };
    
    // Start trying to play after DOM is ready
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        tryPlayIntro();
      });
    });
    
    // Wait for next frame to ensure DOM is fully rendered
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Load Flourish embed script after DOM is ready
        loadFlourishEmbed();
        
        // Initialize policy cards scroll indicators
        initPolicyScrollIndicators();
        initStrengthsScrollIndicators();
        initWeaknessesScrollIndicators();
        
        // Also reset scroll after ScrollTrigger refresh
        setTimeout(() => {
          if (policyScrollContainer) {
            policyScrollContainer.scrollLeft = 0;
          }
          if (strengthsScrollContainer) {
            strengthsScrollContainer.scrollLeft = 0;
          }
          if (weaknessesScrollContainer) {
            weaknessesScrollContainer.scrollLeft = 0;
          }
        }, 500);
        
        // Only refresh ScrollTrigger after intro video finishes
        // This will be called in handleIntroVideoEnd
      });
    });
    
    // Handle window resize
    const handleResize = () => {
      if (window.resizeTimeoutId) clearTimeout(window.resizeTimeoutId);
      window.resizeTimeoutId = setTimeout(() => {
        ScrollTrigger.refresh(true);
        initPolicyScrollIndicators();
        initStrengthsScrollIndicators();
        initWeaknessesScrollIndicators();
      }, 250);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Add click outside handler for menus
    document.addEventListener('click', handleClickOutside);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('click', handleClickOutside);
      ScrollTrigger.getAll().forEach(st => st.kill());
    };
  });

  function initPolicyScrollIndicators() {
    if (!policyScrollContainer || !scrollIndicators) return;
    
    const policyGrid = policyScrollContainer.querySelector('.policy-grid');
    if (!policyGrid) return;
    
    const cards = policyGrid.querySelectorAll('.country-card');
    const cardCount = cards.length;
    
    // Find the indicator-dot container
    const indicatorDot = scrollIndicators.querySelector('.indicator-dot');
    if (!indicatorDot) return;
    
    // Show indicators on all screen sizes
    
    // Update aria-valuemax
    indicatorDot.setAttribute('aria-valuemax', cardCount);
    
    // Create indicators
    indicatorDot.innerHTML = '';
    for (let i = 0; i < cardCount; i++) {
      const indicator = document.createElement('div');
      indicator.className = 'indicator-dot-step';
      if (i === 0) indicator.classList.add('active');
      
      // Add click handler to scroll to card
      indicator.addEventListener('click', () => {
        const card = cards[i];
        if (card) {
          const cardLeft = card.offsetLeft;
          const containerPadding = 16; // 1rem padding
          policyScrollContainer.scrollTo({
            left: cardLeft - containerPadding,
            behavior: 'smooth'
          });
        }
      });
      
      indicatorDot.appendChild(indicator);
    }
    
    // Handle scroll to update indicators
    const updateIndicators = () => {
      const scrollLeft = policyScrollContainer.scrollLeft;
      const containerWidth = policyScrollContainer.offsetWidth;
      const indicators = indicatorDot.querySelectorAll('.indicator-dot-step');
      
      // Calculate scroll progress (0 to 1)
      const maxScroll = policyScrollContainer.scrollWidth - containerWidth;
      const scrollProgress = maxScroll > 0 ? scrollLeft / maxScroll : 0;
      
      // Find which card is most centered/visible
      let mostVisibleIndex = 0;
      let minDistanceToCenter = Infinity;
      const viewportCenter = scrollLeft + containerWidth / 2;
      
      cards.forEach((card, index) => {
        const cardLeft = card.offsetLeft;
        const cardWidth = card.offsetWidth;
        const cardCenter = cardLeft + cardWidth / 2;
        const distanceToCenter = Math.abs(viewportCenter - cardCenter);
        
        if (distanceToCenter < minDistanceToCenter) {
          minDistanceToCenter = distanceToCenter;
          mostVisibleIndex = index;
        }
      });
      
      // Update aria-valuenow
      indicatorDot.setAttribute('aria-valuenow', mostVisibleIndex + 1);
      
      indicators.forEach((indicator, index) => {
        if (index === mostVisibleIndex) {
          indicator.classList.add('active');
        } else {
          indicator.classList.remove('active');
        }
      });
    };
    
    // Throttle scroll events for better performance
    let scrollTimeout;
    const handleScroll = () => {
      if (scrollTimeout) {
        cancelAnimationFrame(scrollTimeout);
      }
      scrollTimeout = requestAnimationFrame(updateIndicators);
    };
    
    policyScrollContainer.addEventListener('scroll', handleScroll);
    
    // Handle touch events to allow vertical scrolling when appropriate
    let touchStartX = 0;
    let touchStartY = 0;
    let isHorizontalScroll = false;
    let touchMoved = false;
    
    const handleTouchStart = (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      isHorizontalScroll = false;
      touchMoved = false;
    };
    
    const handleTouchMove = (e) => {
      if (!touchStartX || !touchStartY) return;
      
      touchMoved = true;
      const touchCurrentX = e.touches[0].clientX;
      const touchCurrentY = e.touches[0].clientY;
      
      const diffX = Math.abs(touchCurrentX - touchStartX);
      const diffY = Math.abs(touchCurrentY - touchStartY);
      
      // Determine scroll direction after a threshold
      if (!isHorizontalScroll && diffX > 10 && diffX > diffY) {
        isHorizontalScroll = true;
      }
      
      // If it's a vertical scroll, allow it to propagate to the page
      if (!isHorizontalScroll && diffY > diffX && diffY > 10) {
        // Don't prevent default - let the page scroll vertically
        return;
      }
    };
    
    const handleTouchEnd = (e) => {
      // If we didn't move much or it was vertical, allow click events
      if (!touchMoved || !isHorizontalScroll) {
        // Allow normal behavior
      }
      touchStartX = 0;
      touchStartY = 0;
      isHorizontalScroll = false;
      touchMoved = false;
    };
    
    policyScrollContainer.addEventListener('touchstart', handleTouchStart, { passive: true });
    policyScrollContainer.addEventListener('touchmove', handleTouchMove, { passive: true });
    policyScrollContainer.addEventListener('touchend', handleTouchEnd, { passive: true });
    
    // Function to reset scroll to start
    const resetScroll = () => {
      if (policyScrollContainer) {
        policyScrollContainer.scrollLeft = 0;
      }
    };
    
    // Ensure scroll starts at the beginning (left) - do this immediately and after render
    resetScroll();
    
    // Also reset after a delay to ensure it sticks
    setTimeout(resetScroll, 50);
    setTimeout(resetScroll, 100);
    setTimeout(resetScroll, 300);
    
    // Use IntersectionObserver to reset scroll when section becomes visible
    if (typeof IntersectionObserver !== 'undefined' && policyScrollContainer) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Reset scroll when section becomes visible
            setTimeout(resetScroll, 100);
          }
        });
      }, {
        threshold: 0.1
      });
      
      observer.observe(policyScrollContainer);
    }
    
    // Initial update
    updateIndicators();
  }
  
  function initStrengthsScrollIndicators() {
    if (!strengthsScrollContainer || !strengthsScrollIndicators) return;
    
    const policyGrid = strengthsScrollContainer.querySelector('.policy-grid');
    if (!policyGrid) return;
    
    const cards = policyGrid.querySelectorAll('.country-card');
    const cardCount = cards.length;
    
    const indicatorDot = strengthsScrollIndicators.querySelector('.indicator-dot');
    if (!indicatorDot) return;
    
    indicatorDot.setAttribute('aria-valuemax', cardCount);
    
    indicatorDot.innerHTML = '';
    for (let i = 0; i < cardCount; i++) {
      const indicator = document.createElement('div');
      indicator.className = 'indicator-dot-step';
      if (i === 0) indicator.classList.add('active');
      
      indicator.addEventListener('click', () => {
        const card = cards[i];
        if (card) {
          const cardLeft = card.offsetLeft;
          const containerPadding = 16;
          strengthsScrollContainer.scrollTo({
            left: cardLeft - containerPadding,
            behavior: 'smooth'
          });
        }
      });
      
      indicatorDot.appendChild(indicator);
    }
    
    const updateIndicators = () => {
      const scrollLeft = strengthsScrollContainer.scrollLeft;
      const containerWidth = strengthsScrollContainer.offsetWidth;
      const indicators = indicatorDot.querySelectorAll('.indicator-dot-step');
      
      const maxScroll = strengthsScrollContainer.scrollWidth - containerWidth;
      const scrollProgress = maxScroll > 0 ? scrollLeft / maxScroll : 0;
      
      let mostVisibleIndex = 0;
      let minDistanceToCenter = Infinity;
      const viewportCenter = scrollLeft + containerWidth / 2;
      
      cards.forEach((card, index) => {
        const cardLeft = card.offsetLeft;
        const cardWidth = card.offsetWidth;
        const cardCenter = cardLeft + cardWidth / 2;
        const distanceToCenter = Math.abs(viewportCenter - cardCenter);
        
        if (distanceToCenter < minDistanceToCenter) {
          minDistanceToCenter = distanceToCenter;
          mostVisibleIndex = index;
        }
      });
      
      indicatorDot.setAttribute('aria-valuenow', mostVisibleIndex + 1);
      
      indicators.forEach((indicator, index) => {
        if (index === mostVisibleIndex) {
          indicator.classList.add('active');
        } else {
          indicator.classList.remove('active');
        }
      });
    };
    
    let scrollTimeout;
    const handleScroll = () => {
      if (scrollTimeout) {
        cancelAnimationFrame(scrollTimeout);
      }
      scrollTimeout = requestAnimationFrame(updateIndicators);
    };
    
    strengthsScrollContainer.addEventListener('scroll', handleScroll);
    
    const resetScroll = () => {
      if (strengthsScrollContainer) {
        strengthsScrollContainer.scrollLeft = 0;
      }
    };
    
    resetScroll();
    setTimeout(resetScroll, 50);
    setTimeout(resetScroll, 100);
    setTimeout(resetScroll, 300);
    
    if (typeof IntersectionObserver !== 'undefined' && strengthsScrollContainer) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setTimeout(resetScroll, 100);
          }
        });
      }, {
        threshold: 0.1
      });
      
      observer.observe(strengthsScrollContainer);
    }
    
    updateIndicators();
  }
  
  function initWeaknessesScrollIndicators() {
    if (!weaknessesScrollContainer || !weaknessesScrollIndicators) return;
    
    const policyGrid = weaknessesScrollContainer.querySelector('.policy-grid');
    if (!policyGrid) return;
    
    const cards = policyGrid.querySelectorAll('.country-card');
    const cardCount = cards.length;
    
    const indicatorDot = weaknessesScrollIndicators.querySelector('.indicator-dot');
    if (!indicatorDot) return;
    
    indicatorDot.setAttribute('aria-valuemax', cardCount);
    
    indicatorDot.innerHTML = '';
    for (let i = 0; i < cardCount; i++) {
      const indicator = document.createElement('div');
      indicator.className = 'indicator-dot-step';
      if (i === 0) indicator.classList.add('active');
      
      indicator.addEventListener('click', () => {
        const card = cards[i];
        if (card) {
          const cardLeft = card.offsetLeft;
          const containerPadding = 16;
          weaknessesScrollContainer.scrollTo({
            left: cardLeft - containerPadding,
            behavior: 'smooth'
          });
        }
      });
      
      indicatorDot.appendChild(indicator);
    }
    
    const updateIndicators = () => {
      const scrollLeft = weaknessesScrollContainer.scrollLeft;
      const containerWidth = weaknessesScrollContainer.offsetWidth;
      const indicators = indicatorDot.querySelectorAll('.indicator-dot-step');
      
      const maxScroll = weaknessesScrollContainer.scrollWidth - containerWidth;
      const scrollProgress = maxScroll > 0 ? scrollLeft / maxScroll : 0;
      
      let mostVisibleIndex = 0;
      let minDistanceToCenter = Infinity;
      const viewportCenter = scrollLeft + containerWidth / 2;
      
      cards.forEach((card, index) => {
        const cardLeft = card.offsetLeft;
        const cardWidth = card.offsetWidth;
        const cardCenter = cardLeft + cardWidth / 2;
        const distanceToCenter = Math.abs(viewportCenter - cardCenter);
        
        if (distanceToCenter < minDistanceToCenter) {
          minDistanceToCenter = distanceToCenter;
          mostVisibleIndex = index;
        }
      });
      
      indicatorDot.setAttribute('aria-valuenow', mostVisibleIndex + 1);
      
      indicators.forEach((indicator, index) => {
        if (index === mostVisibleIndex) {
          indicator.classList.add('active');
        } else {
          indicator.classList.remove('active');
        }
      });
    };
    
    let scrollTimeout;
    const handleScroll = () => {
      if (scrollTimeout) {
        cancelAnimationFrame(scrollTimeout);
      }
      scrollTimeout = requestAnimationFrame(updateIndicators);
    };
    
    weaknessesScrollContainer.addEventListener('scroll', handleScroll);
    
    const resetScroll = () => {
      if (weaknessesScrollContainer) {
        weaknessesScrollContainer.scrollLeft = 0;
      }
    };
    
    resetScroll();
    setTimeout(resetScroll, 50);
    setTimeout(resetScroll, 100);
    setTimeout(resetScroll, 300);
    
    if (typeof IntersectionObserver !== 'undefined' && weaknessesScrollContainer) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setTimeout(resetScroll, 100);
          }
        });
      }, {
        threshold: 0.1
      });
      
      observer.observe(weaknessesScrollContainer);
    }
    
    updateIndicators();
  }
  
  function updateBackground(color) {
    currentBackground = color;
  }
  
  function handleIntroVideoEnd() {
    introVideoFinished = true;
    document.body.style.overflow = '';
    // Hide video smoothly to reveal sequence behind it
    if (introVideoElement) {
      introVideoElement.style.opacity = '0';
      introVideoElement.style.transition = 'opacity 0.3s ease';
      setTimeout(() => {
        if (introVideoElement) {
          introVideoElement.style.display = 'none';
        }
      }, 300);
    }
    // Refresh ScrollTrigger after intro video finishes
    ScrollTrigger.refresh(true);
  }
</script>

<!-- Background container for visual elements -->
<div 
  id="container" 
  bind:this={backgroundContainer}
  class="bg-{currentBackground}"
></div>

<div class="scroll-sections">
  <!-- Group 1: Welcome and Overview -->
  <ScrollSection 
    groupId="group1" 
    backgroundColors={backgroundColors}
    onBackgroundChange={updateBackground}
  >
    <Header />
    
    <!-- Article Header Panel - overlay on video from start -->
    <div class="panel" id="panel1">
      <div class="content-box article-header-overlay">
        <h1 class="article-title">How will the EV journey in South-east Asia unfold?</h1>
        
        <p class="article-byline">by <a href="https://www.businesstimes.com.sg/authors/evan-see">EVAN SEE</a>, <a href="https://www.businesstimes.com.sg/authors/david-li-zuowei">ZUOWEI LI</a>, <a href="https://www.businesstimes.com.sg/authors/kimberly-wee">KIMBERLY WEE</a>, and <a href="https://www.businesstimes.com.sg/authors/mindy-tan">MINDY TAN</a></p>
        <p class="article-published">PUBLISHED 16 NOVEMBER, 2025 • 11:00 AM</p>
      </div>
    </div>
    
    <!-- Video Section - contains intro video first, then scroll sequence -->
    <div class="video-section">
      {#if !introVideoFinished}
        <!-- Intro Video - plays on landing -->
        <video
          bind:this={introVideoElement}
          class="intro-video"
          src="./intro.mp4"
          autoplay
          muted
          playsinline
          on:ended={handleIntroVideoEnd}
        ></video>
      {/if}
      <!-- Scroll-Mapped Sequence Animation - plays on scroll after intro -->
      <!-- Always render but hidden behind video, will be controlled by scroll -->
      <div class="sequence-container">
        <VideoAnimation
          width="100%"
          height="100vh"
          scrollMapped={true}
          scrollMultiplier={0.2}
          scrubDelay={0.28}
          sequenceBasePath="./sequence7/ev_sequence"
          totalFrames={380}
        />
      </div>
    </div>
    
    <!-- Text Panel 2: The Challenge -->
    <div class="panel" id="panel2">
      <div class="content-box">
        
        <p> South-east Asia’s electric vehicle (EV) revolution has hit a speed bump.</p>
      </div>
    </div>

    <!-- Text Panel 3: Thailand -->
    <div class="panel" id="panel3">
      <div class="content-box">
        
        <p>Insufficient EV infrastructure, inconsistent government policies and affordability issues have dampened demand in most countries.</p>
      </div>
    </div>
    
    <!-- Text Panel 4: Malaysia -->
    <div class="panel" id="panel4">
      <div class="content-box">
        
        <p>In Malaysia, EVs make up just 4.2% of total vehicle sales. In Indonesia, demand for electric motorcycles has slowed due to sluggish consumer spending and uncertainty over government incentives.</p>
      </div>
    </div>

    <!-- Text Panel 5: Indonesia -->
    <div class="panel" id="panel5">
      <div class="content-box">
        
        <p>While the drive has been less than smooth in the region, there are some bright spots.</p>
      </div>
    </div>

    <!-- Text Panel 6: Bright Spots -->
    <div class="panel" id="panel6">
      <div class="content-box">
        
        <p>Driven by local producer VinFast, 27 per cent of Vietnam’s four-wheeler sales in 2025's first nine months were EVs, among the region's highest.</p>
      </div>
    </div>

    <!-- Text Panel 7: Vietnam Success -->
    <div class="panel" id="panel7">
      <div class="content-box">
        
        <p>How fast – and far – can South-east Asia go in its EV journey?</p>
      </div>
    </div>

  </ScrollSection>
  
  
  <!-- Group 2: Interactive Elements -->
  <ScrollSection 
    groupId="group2" 
    backgroundColors={backgroundColors}
    onBackgroundChange={updateBackground}
  >
    <!-- Government Targets Content -->
    <div class="article-content">
      <div class="article-text">
        <h3>As S-E Asia’s EV plans accelerate, consumer realities pose a bumpy road</h3>
        
        <p>Governments across South-east Asia have set ambitious EV targets.</p>
        
        <p>Singapore's Green Plan 2030, for example, has aimed for all new car registrations to be electricity-powered or hybrid vehicles by 2030. Thailand, the region's largest EV sales market, has targeted electricity-powered vehicles to make up 30 per cent of all auto production by the turn of the decade.</p>
        
        <p>Come 2040, Singapore aims to fully phase out internal combustion engine (ICE) vehicles from its roads. By the same year, its neighbour the Philippines aims for half of all vehicles to be electrified.</p>
        
        <!-- EV Policy Targets Visualization -->
        <div class="panel_cards" id="panel5_3">
      <div class="content-box">
        <h2>EV policy targets across South-east Asia</h2>
        <div class="policy-visualization">
          <!-- Mobile country selector with hamburger -->
          <div class="country-selector">
            <button 
              class="hamburger-button" 
              on:click|stopPropagation={togglePolicyMenu}
              aria-label="Select country"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 12H21" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <path d="M3 6H21" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <path d="M3 18H21" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
              <span class="selected-country">{selectedPolicyCountry}</span>
              <span class="tap-hint">Tap to select</span>
            </button>
            {#if policyMenuOpen}
              <div class="country-menu">
                {#each countries as country}
                  <button 
                    class="country-menu-item" 
                    class:active={selectedPolicyCountry === country}
                    on:click|stopPropagation={() => selectPolicyCountry(country)}
                  >
                    {country}
                  </button>
                {/each}
              </div>
            {/if}
          </div>
          <div class="policy-scroll-container" bind:this={policyScrollContainer}>
            <div class="policy-grid">
              <div class="country-card" data-country="Indonesia" class:mobile-hidden={selectedPolicyCountry !== 'Indonesia'}>
              <div class="country-name-new">Indonesia</div>
              <div class="adoption-section">
                <div class="section-heading">ADOPTION</div>
                <div class="adoption-item">
                  <img src="./images/car.png" class="ev-icon car-icon" alt="Car" />
                  <div class="stat-number">2m</div>
                  <div class="stat-description">electric cars on roads by 2030</div>
                </div>
                <div class="adoption-item">
                  <img src="./images/motorcycle.png" class="ev-icon motorcycle-icon" alt="Motorcycle" />
                  <div class="stat-number">13m</div>
                  <div class="stat-description">electric motorcycles on roads by 2030</div>
                </div>
              </div>
              <div class="production-section">
                <div class="section-heading">PRODUCTION</div>
                <div class="production-item">
                  <img src="./images/production.png" class="ev-icon production-icon" alt="Production" />
                  <div class="stat-number">600k</div>
                  <div class="stat-description">electric cars produced domestically by 2030</div>
                </div>
              </div>
            </div>
            
            <div class="country-card" data-country="Philippines" class:mobile-hidden={selectedPolicyCountry !== 'Philippines'}>
              <div class="country-name-new">Philippines</div>
              <div class="adoption-section">
                <div class="section-heading">ADOPTION</div>
                <div class="adoption-item">
                  <img src="./images/car.png" class="ev-icon car-icon" alt="Car" />
                  <div class="stat-number">300k</div>
                  <div class="stat-description">electric cars on roads by 2030</div>
                </div>
                <div class="adoption-item">
                  <img src="./images/motorcycle.png" class="ev-icon motorcycle-icon" alt="Motorcycle" />
                  <div class="stat-number">3.6m</div>
                  <div class="stat-description">electric motorcycles on roads by 2030</div>
                </div>
              </div>
              <div class="production-section">
                <div class="section-heading">INFRASTRUCTURE</div>
                <div class="production-item">
                  <img src="./images/infrastructure.png" class="ev-icon infrastructure-icon" alt="Infrastructure" />
                  <div class="stat-number">7.3k</div>
                  <div class="stat-description">EV charging stations by 2028</div>
                </div>
              </div>
            </div>
            
            <div class="country-card" data-country="Malaysia" class:mobile-hidden={selectedPolicyCountry !== 'Malaysia'}>
              <div class="country-name-new">Malaysia</div>
              <div class="adoption-section">
                <div class="section-heading">ADOPTION</div>
                <div class="adoption-item">
                  <img src="./images/car.png" class="ev-icon car-icon" alt="Car" />
                  <div class="stat-number">20%</div>
                  <div class="stat-description">of new car sales to be EV/HEV by 2030</div>
                </div>
              </div>
              <div class="production-section">
                <div class="section-heading">PRODUCTION</div>
                <div class="production-item">
                  <img src="./images/production.png" class="ev-icon production-icon" alt="Production" />
              
                  <div class="stat-description">EV roll-outs by national automakers, Perodua and Proton</div>
                </div>
              </div>
            </div>
            
            <div class="country-card" data-country="Singapore" class:mobile-hidden={selectedPolicyCountry !== 'Singapore'}>
              <div class="country-name-new">Singapore</div>
              <div class="adoption-section">
                <div class="section-heading">ADOPTION</div>
                <div class="adoption-item">
                  <img src="./images/car.png" class="ev-icon car-icon" alt="Car" />
                  <div class="stat-number">100%</div>
                  <div class="stat-description">of new car registrations to be EV/HEV by 2030</div>
                </div>
                <div class="adoption-item">

                  <div class="stat-number">100%</div>
                  <div class="stat-description">of cars on road to be non-ICE by 2040</div>
                </div>
              </div>
              <div class="production-section">
                <div class="section-heading">INFRASTRUCTURE</div>
                <div class="production-item">
                  <img src="./images/infrastructure.png" class="ev-icon infrastructure-icon" alt="Infrastructure" />
                  <div class="stat-number">600k</div>
                  <div class="stat-description">EV charging stations by 2030</div>
                </div>
              </div>
            </div>
            
            <div class="country-card" data-country="Thailand" class:mobile-hidden={selectedPolicyCountry !== 'Thailand'}>
              <div class="country-name-new">Thailand</div>
              <div class="adoption-section">
                <div class="section-heading">PRODUCTION</div>
                <div class="adoption-item">
                  <img src="./images/production.png" class="ev-icon production-icon" alt="Production" />
                  <div class="stat-number">30%</div>
                  <div class="stat-description">of domestically manufactured vehicles to be EV by 2030</div>
                </div>
                <div class="adoption-item">
                  <img src="./images/car.png" class="ev-icon car-icon" alt="Car" />
                  <div class="stat-number">725k</div>
                  <div class="stat-description">electric cars manufactured by 2030</div>
                </div>
                <div class="adoption-item">
                  <img src="./images/motorcycle.png" class="ev-icon motorcycle-icon" alt="Motorcycle" />
                  <div class="stat-number">675k</div>
                  <div class="stat-description">electric motorcycles manufactured by 2030</div>
                </div>
              </div>
            </div>
            
            <div class="country-card" data-country="Vietnam" class:mobile-hidden={selectedPolicyCountry !== 'Vietnam'}>
              <div class="country-name-new">Vietnam</div>
              <div class="adoption-section">
                <div class="section-heading">ADOPTION</div>
                <div class="adoption-item">
                  <img src="./images/car.png" class="ev-icon car-icon" alt="Car" />
                  <div class="stat-number">30%</div>
                  <div class="stat-description">of cars on roads to be EV by 2030</div>
                </div>
                <div class="adoption-item">
                  <img src="./images/motorcycle.png" class="ev-icon motorcycle-icon" alt="Motorcycle" />
                  <div class="stat-number">22%</div>
                  <div class="stat-description">of motorcycles on roads to be EV by 2030</div>
                </div>
              </div>
              <div class="production-section">
                <div class="section-heading">INFRASTRUCTURE</div>
                <div class="production-item">
                  <img src="./images/infrastructure.png" class="ev-icon infrastructure-icon" alt="Infrastructure" />
                  <div class="stat-description">Improving charging infrastructure</div>
                </div>
              </div>
            </div>
            </div>
            <div style="margin-top: 15px" bind:this={scrollIndicators}>
              <div role="progressbar" class="indicator-dot" aria-valuemin="1" aria-valuemax="6">
                <!-- Indicators will be generated by JavaScript -->
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
        
        <p>But translating production targets into take-up rates may not be a smooth road. In the case of Thailand, research analyst for autos at Bank of America Jessie Lo expects user penetration among four-wheelers in the kingdom to reach 18 per cent by 2030 – far behind target – with similar Asean-wide projections at 17 per cent.</p>
      </div>
    </div>
  </ScrollSection>


  
  <!-- Article Content Section -->
  <div class="article-content">
    <div class="article-text">
      
      <!-- Flourish Visualization -->
      <div class="flourish-embed" data-src="visualisation/25799655" bind:this={flourishEmbedContainer}></div>
      
      <p>"Lack of charging infrastructure would still be the biggest bottleneck," Lo noted, adding that Asean governments do not have enough incentives in place to fund large charging infrastructure projects.</p>
      
      <p>Geraldina Koh, country manager in Thailand for charging provider Eigen Energy, told <em>The Business Times</em> that long sales cycles and significant capital expenditures are needed for charging projects, which has made it difficult for companies to sustain businesses.</p>
      
      <p>"Some projects take up to a year to move from pitch to installation," said Koh. "Even with tax waivers that you claim after your project meets certain targets, you still need to fork out the upfront costs to get the project started."</p>
      
      <p>Nguyen Ba Canh Son, founder and chief executive of Vietnamese electric motorcycle manufacturer Dat Bike, said a vicious circle therefore ensues, with insufficient infrastructure holding users back from taking up EVs.</p>
      
      <p>"If there's more infrastructure today, a lot more people will switch to EVs," said Son. "If a lot more people are using EVs, it's easier for investors to invest in infrastructure."</p>
      
      <p>Bank of America's Lo noted that less-developed regions in countries such as the Philippines, Vietnam and Indonesia also face a lack of reliability in grid infrastructure. "In Indonesia, 90 to 95 per cent of households do not have the minimum power load to charge EVs, which limits adoption rates," she added.</p>
      
      <p>Elsewhere, government messaging towards the EV transition has not always been cohesive beyond setting baseline targets, said Akshay Prasad, principal at management consultancy firm Arthur D Little's automotive practice.</p>
      
      <p>For instance, moves to subsidise petroleum in countries such as Indonesia and Malaysia have sent conflicting messages to stakeholders in the EV space, he noted. Malaysia's EV sales have remained a relatively shallow part of the country's automotive market compared to its Asean peers, as government direction on market subsidies continues to hang in the balance.</p>
      
      <!-- Market Strengths Visualization -->
      <div class="panel_cards" id="panel5_4">
        <div class="content-box">
          <h2>Market strengths by country</h2>
          <div class="policy-visualization">
            <!-- Mobile country selector with hamburger -->
            <div class="country-selector">
              <button 
                class="hamburger-button" 
                on:click|stopPropagation={toggleStrengthsMenu}
                aria-label="Select country"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 12H21" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  <path d="M3 6H21" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  <path d="M3 18H21" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
                <span class="selected-country">{selectedStrengthsCountry}</span>
                <span class="tap-hint">Tap to select</span>
              </button>
              {#if strengthsMenuOpen}
                <div class="country-menu">
                  {#each countries as country}
                    <button 
                      class="country-menu-item" 
                      class:active={selectedStrengthsCountry === country}
                      on:click|stopPropagation={() => selectStrengthsCountry(country)}
                    >
                      {country}
                    </button>
                  {/each}
                </div>
              {/if}
            </div>
            <div class="policy-scroll-container" bind:this={strengthsScrollContainer}>
              <div class="policy-grid">
                <div class="country-card" data-country="Indonesia" class:mobile-hidden={selectedStrengthsCountry !== 'Indonesia'}>
                  <div class="country-name-new">Indonesia</div>
                  <div class="adoption-section">
                    <div class="section-heading">ADOPTION</div>
                    <div class="adoption-item">
                      <img src="./images/car.png" class="ev-icon car-icon" alt="Car" />
                      <div class="stat-description">Largest auto market in Asean by sales</div>
                    </div>
                  </div>
                  <div class="production-section">
                    <div class="section-heading">RESOURCES</div>
                    <div class="production-item">
                      <img src="./images/nickel.png" class="ev-icon" alt="Nickel" />
                      <div class="stat-description">Largest nickel reserves for battery production</div>
                    </div>
                  </div>
                </div>
                
                <div class="country-card" data-country="Philippines" class:mobile-hidden={selectedStrengthsCountry !== 'Philippines'}>
                  <div class="country-name-new">Philippines</div>
                  <div class="adoption-section">
                    <div class="section-heading">ADOPTION</div>
                    <div class="adoption-item">
                      <img src="./images/car.png" class="ev-icon car-icon" alt="Car" />
                      <div class="stat-description">Strong auto growth potential</div>
                    </div>
                  </div>
                  <div class="production-section">
                    <div class="section-heading">RESOURCES</div>
                    <div class="production-item">
                      <img src="./images/nickel.png" class="ev-icon" alt="Nickel" />
                      <div class="stat-description">Nickel deposits offer potential for battery manufacturing</div>
                    </div>
                  </div>
                  <div class="production-section">
                    <div class="section-heading">POLICY</div>
                    <div class="production-item">
                      <img src="./images/government.png" class="ev-icon" alt="Government" />
                      <div class="stat-description">Attractive government incentives for suppliers</div>
                    </div>
                  </div>
                </div>
                
                <div class="country-card" data-country="Malaysia" class:mobile-hidden={selectedStrengthsCountry !== 'Malaysia'}>
                  <div class="country-name-new">Malaysia</div>
                  <div class="adoption-section">
                    <div class="section-heading">PRODUCTION</div>
                    <div class="adoption-item">
                      <img src="./images/production.png" class="ev-icon production-icon" alt="Production" />
                      <div class="stat-description">Robust electronics and auto manufacturing base</div>
                    </div>
                  </div>
                </div>
                
                <div class="country-card" data-country="Singapore" class:mobile-hidden={selectedStrengthsCountry !== 'Singapore'}>
                  <div class="country-name-new">Singapore</div>
                  <div class="adoption-section">
                    <div class="section-heading">INFRASTRUCTURE</div>
                    <div class="adoption-item">
                      <img src="./images/infrastructure.png" class="ev-icon infrastructure-icon" alt="Infrastructure" />
                      <div class="stat-description">Strong charging infrastructure and government support</div>
                    </div>
                  </div>
                </div>
                
                <div class="country-card" data-country="Thailand" class:mobile-hidden={selectedStrengthsCountry !== 'Thailand'}>
                  <div class="country-name-new">Thailand</div>
                  <div class="adoption-section">
                    <div class="section-heading">PRODUCTION</div>
                    <div class="adoption-item">
                      <img src="./images/production.png" class="ev-icon production-icon" alt="Production" />
                      <div class="stat-description">Largest manufacturing base</div>
                    </div>
                  </div>
                </div>
                
                <div class="country-card" data-country="Vietnam" class:mobile-hidden={selectedStrengthsCountry !== 'Vietnam'}>
                  <div class="country-name-new">Vietnam</div>
                  <div class="adoption-section">
                    <div class="section-heading">ADOPTION</div>
                    <div class="adoption-item">
                      <img src="./images/motorcycle.png" class="ev-icon motorcycle-icon" alt="Motorcycle" />
                      <div class="stat-description">High electric two-wheeler adoption</div>
                    </div>
                  </div>
                  <div class="production-section">
                    <div class="section-heading">PRODUCTION</div>
                    <div class="production-item">
                      <img src="./images/production.png" class="ev-icon production-icon" alt="Production" />
                      <div class="stat-description">Market is led by strong domestic EV brands</div>
                    </div>
                  </div>
                </div>
              </div>
              <div style="margin-top: 15px" bind:this={strengthsScrollIndicators}>
                <div role="progressbar" class="indicator-dot" aria-valuemin="1" aria-valuemax="6">
                  <!-- Indicators will be generated by JavaScript -->
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Market Weaknesses Visualization -->
      <div class="panel_cards" id="panel5_5">
        <div class="content-box">
          <h2>Market weaknesses by country</h2>
          <div class="policy-visualization">
            <!-- Mobile country selector with hamburger -->
            <div class="country-selector">
              <button 
                class="hamburger-button" 
                on:click|stopPropagation={toggleWeaknessesMenu}
                aria-label="Select country"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 12H21" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  <path d="M3 6H21" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  <path d="M3 18H21" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
                <span class="selected-country">{selectedWeaknessesCountry}</span>
                <span class="tap-hint">Tap to select</span>
              </button>
              {#if weaknessesMenuOpen}
                <div class="country-menu">
                  {#each countries as country}
                    <button 
                      class="country-menu-item" 
                      class:active={selectedWeaknessesCountry === country}
                      on:click|stopPropagation={() => selectWeaknessesCountry(country)}
                    >
                      {country}
                    </button>
                  {/each}
                </div>
              {/if}
            </div>
            <div class="policy-scroll-container" bind:this={weaknessesScrollContainer}>
              <div class="policy-grid">
                <div class="country-card" data-country="Indonesia" class:mobile-hidden={selectedWeaknessesCountry !== 'Indonesia'}>
                  <div class="country-name-new">Indonesia</div>
                  <div class="adoption-section">
                    <div class="section-heading">FINANCING</div>
                    <div class="adoption-item">
                      <img src="./images/finance.png" class="ev-icon" alt="Finance" />
                      <div class="stat-description">Limited consumer access to financing</div>
                    </div>
                    <div class="production-item" style="margin-top: 0.75rem;">
                      <div class="stat-description">Highly price-sensitive market</div>
                    </div>
                  </div>
                  <div class="production-section">
                    <div class="section-heading">INFRASTRUCTURE</div>

                    <div class="production-item" style="margin-top: 0.75rem;">
                      <img src="./images/infrastructure.png" class="ev-icon infrastructure-icon" alt="Infrastructure" />
                      <div class="stat-description">Limited charging infrastructure</div>
                    </div>
                  </div>
                </div>
                
                <div class="country-card" data-country="Philippines" class:mobile-hidden={selectedWeaknessesCountry !== 'Philippines'}>
                  <div class="country-name-new">Philippines</div>
                  <div class="adoption-section">
                    <div class="section-heading">FINANCING</div>
                    <div class="adoption-item">
                      <img src="./images/finance.png" class="ev-icon" alt="Finance" />
                      <div class="stat-description">EVs remain unaffordable with limited subsidies</div>
                    </div>
                  </div>
                  <div class="production-section">
                    <div class="section-heading">INFRASTRUCTURE</div>
                    <div class="production-item">
                      <img src="./images/infrastructure.png" class="ev-icon infrastructure-icon" alt="Infrastructure" />
                      <div class="stat-description">Limited charging infrastructure outside bigger cities</div>
                    </div>
                    <div class="production-item" style="margin-top: 0.75rem;">
                      <div class="stat-description">Smaller auto manufacturing base</div>
                    </div>
                  </div>
                </div>
                
                <div class="country-card" data-country="Malaysia" class:mobile-hidden={selectedWeaknessesCountry !== 'Malaysia'}>
                  <div class="country-name-new">Malaysia</div>
                  <div class="adoption-section">
                    <div class="section-heading">POLICY</div>
                    <div class="adoption-item">
                      <img src="./images/car.png" class="ev-icon car-icon" alt="Car" />
                      <div class="stat-description">Fuel subsidies hinder EV adoption</div>
                    </div>
                  </div>
                  <div class="production-section">
                    <div class="section-heading">INFRASTRUCTURE</div>
                    <div class="production-item">
                      <img src="./images/infrastructure.png" class="ev-icon infrastructure-icon" alt="Infrastructure" />
                      <div class="stat-description">Charging infrastructure faces financing difficulties</div>
                    </div>
                  </div>
                </div>
                
                <div class="country-card" data-country="Singapore" class:mobile-hidden={selectedWeaknessesCountry !== 'Singapore'}>
                  <div class="country-name-new">Singapore</div>
                  <div class="adoption-section">
                    <div class="section-heading">POLICY</div>
                    <div class="adoption-item">
                      <img src="./images/car.png" class="ev-icon car-icon" alt="Car" />
                      <div class="stat-description">Car-lite government objectives and high COE prices may slow overall auto growth</div>
                    </div>
                  </div>
                </div>
                
                <div class="country-card" data-country="Thailand" class:mobile-hidden={selectedWeaknessesCountry !== 'Thailand'}>
                  <div class="country-name-new">Thailand</div>
                  <div class="adoption-section">
                    <div class="section-heading">FINANCING</div>
                    <div class="adoption-item">
                      <img src="./images/finance.png" class="ev-icon" alt="Finance" />
                      <div class="stat-description">Heavy household debt may slow access to auto financing</div>
                    </div>
                  </div>
                  <div class="production-section">
                    <div class="section-heading">INFRASTRUCTURE</div>
                    <div class="production-item">
                      <img src="./images/infrastructure.png" class="ev-icon infrastructure-icon" alt="Infrastructure" />
                      <div class="stat-description">Incentives are focused more on manufacturers than charging infrastructure</div>
                    </div>
                  </div>
                </div>
                
                <div class="country-card" data-country="Vietnam" class:mobile-hidden={selectedWeaknessesCountry !== 'Vietnam'}>
                  <div class="country-name-new">Vietnam</div>
                  <div class="adoption-section">
                    <div class="section-heading">FINANCING</div>
                    <div class="adoption-item">
                      <img src="./images/finance.png" class="ev-icon" alt="Finance" />
                      <div class="stat-description">Limited government financial support for EV production and adoption</div>
                    </div>
                  </div>
                  <div class="production-section">
                    <div class="section-heading">INFRASTRUCTURE</div>
                    <div class="production-item">
                      <img src="./images/infrastructure.png" class="ev-icon infrastructure-icon" alt="Infrastructure" />
                      <div class="stat-description">Inconsistent grid reliability</div>
                    </div>
                  </div>
                </div>
              </div>
              <div style="margin-top: 15px" bind:this={weaknessesScrollIndicators}>
                <div role="progressbar" class="indicator-dot" aria-valuemin="1" aria-valuemax="6">
                  <!-- Indicators will be generated by JavaScript -->
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <h3>Luxury goods</h3>
      
      <p>There is also the question of affordability.</p>
      
      <p>Among four-wheelers, where affordability is a large adoption barrier, Chinese auto manufacturers have been mainstays. Bank of America's Lo said brands such as BYD, Chery and Wuling have captured more than 80 per cent of the EV market share in Thailand, Indonesia and the Philippines, up to 65 per cent in Singapore and 50 per cent in Malaysia.</p>
      
      <p>Several Chinese manufacturers have <a href="https://www.businesstimes.com.sg/companies-markets/transport-logistics/byd-amps-up-brutal-china-ev-price-war-with-bigger-discounts-on-new-car-versions?ref=pulse" target="_blank" rel="noopener noreferrer">offered vehicles at steep discounts</a>, as efforts to <a href="https://www.businesstimes.com.sg/international/asean/lured-thailand-perks-chinas-ev-makers-face-reality-check" target="_blank" rel="noopener noreferrer">seize a larger share</a> of the fiercely contested mainland EV market also <a href="https://www.businesstimes.com.sg/international/asean/lured-thailand-perks-chinas-ev-makers-face-reality-check" target="_blank" rel="noopener noreferrer">creep into South-east Asia</a>.</p>
      
      <p>However, deflationary pressures in China have <a href="https://www.businesstimes.com.sg/companies-markets/transport-logistics/china-warns-byd-rivals-self-regulate-price-war-heats?ref=article-see-also" target="_blank" rel="noopener noreferrer">prompted warnings from authorities</a> to cease such price wars, a move that may further elevate EV prices in the region.</p>
      
      <p>Over in Indonesia, the affordability issue is also dogging electric two-wheeler adoption, particularly following the rollback of the country's two-wheeler subsidy programme which ended late 2024.</p>
      
      <p>This is further compounded by the fact that it remains difficult for riders to secure financing for electric two-wheelers within traditional auto loan frameworks. Banks are often reluctant to accept the level of default risk with these riders, while the residual value of such EVs remains difficult for lenders to assess given their novelty.</p>
      
      <p>Therefore, many smaller startups have turned away from sales in favour of short-term leases to lower-income riders, Prasad of Arthur D Little noted. "This is an obstacle to adoption as there's no stickiness to this consumer base," he said.</p>
      
      <p>Worse, some riders fear they may not even be getting their money's worth by purchasing an e-motorcycle.</p>
      
      <p>"Electric motorcycles need to grant riders the same range, power and speed as an ICE motorcycle, but at the same cost or lower," said Dat Bike's Son. "Currently, the price-to-performance ratio among most manufacturers in South-east Asia is not sufficient to justify making the switch."</p>
      
      <h3>The domestic dilemma</h3>
      
      <p>Currently, Vietnam's VinFast, the listed automaker founded by one of the country's largest conglomerates Vingroup, <a href="https://www.businesstimes.com.sg/international/asean/batteries-not-included-china-ev-makers-struggle-break-vinfasts-dominance-vietnam" target="_blank" rel="noopener noreferrer">remains the only domestic four-wheeler manufacturer</a> in the Asean EV market, where few domestic automakers have been able to rival dominant Chinese players.</p>
      
      <p>Thai state-owned oil and gas company PTT had previously approved a joint venture with Taiwanese tech firm Foxconn to manufacture EVs in Thailand by 2024. However, doubts around the venture's profitability led PTT to cut its stake in the project, leaving current production plans in limbo.</p>
      
      <p>Yet domestic two-wheeler brands such as Vietnam's Dat Bike and Selex, Thailand's Deco and Singapore's Zion have bucked this trend, achieving local and regional presence ahead of Chinese entrants. Dat Bike, for instance, has looked beyond its home market with a <a href="https://www.businesstimes.com.sg/startups-tech/startups/vietnam-startup-dat-bike-seeks-slice-third-largest-ev-motorbike-market" target="_blank" rel="noopener noreferrer">planned entry into Thailand</a> in 2026.</p>
      
      <figure style="margin: 2rem 0; text-align: center;">
        <img src="./images/photo.png" alt="Nguyen Ba Canh Son, founder and CEO of Dat Bike" style="max-width: 100%; height: auto; display: block; margin: 0 auto;" />
        <figcaption style="margin-top: 0.75rem; font-size: 0.9rem; color: #666; text-align: left; max-width: 800px; margin-left: auto; margin-right: auto; font-family: 'Poppins', sans-serif; font-weight: 400;">
          Nguyen Ba Canh Son, founder and CEO of Vietnamese e-motorcycle maker Dat Bike, believes that regionally-produced bikes can take on Chinese manufacturers. PHOTO: DAT BIKE
        </figcaption>
      </figure>
      
      <p>"The motorcycles used in South-east Asia are very different from those used in China, where motorcycles cannot exceed speeds of 45 kilometres per hour," said Son. "Here, riders want electric motorcycles to be the equivalent of ICE motorcycles."</p>
      
      <p>"There's not enough incentive for the Chinese to develop a new product and go after the market here," he added.</p>
      
      <p>Still, Prasad believes that introducing more domestic brands could hold an important key to higher penetration rates, as in the case of Vietnam's VinFast.</p>
      
      <p>"If EVs are truly a national priority, having the domestic machinery can help governments to execute their strategic goals," he said. "Right now, they don't even have that option."</p>
      
      <p>To truly break through bottlenecks, Prasad noted that a more holistic and coordinated approach is needed from Asean governments. "The focus beyond charging infrastructure and demand-side subsidies also has to include subsidising the ecosystem – component manufacturing, battery recycling programmes and EV financing."</p>
      
      <div class="regional-insights-section">
        <h3>Get more regional snapshots and insights into Asean's EV scene with BT Global</h3>
        <div class="regional-insights-container">
          <div class="regional-insight-item">
            <img src="./images/1.png" alt="Malaysia EV insights" class="regional-insight-icon" />
            <div class="regional-insight-content">
              <h4>Affordable EVs are here, but Malaysia still trails Asean peers</h4>
              <a href="https://www.businesstimes.com.sg/international/asean/affordable-evs-have-landed-malaysia-country-still-trails-asean-peers" target="_blank" rel="noopener noreferrer" class="regional-insight-link">READ MORE →</a>
            </div>
          </div>
          
          <div class="regional-insight-item">
            <img src="./images/2.png" alt="Thailand EV insights" class="regional-insight-icon" />
            <div class="regional-insight-content">
              <h4>Can Thailand's auto industry change course to ride the EV wave?</h4>
              <a href="https://businesstimes.com.sg/international/asean/thailands-ev-adoption-grows-its-automotive-market-wrestles-aftershocks" target="_blank" rel="noopener noreferrer" class="regional-insight-link">READ MORE →</a>
            </div>
          </div>
          
          <div class="regional-insight-item">
            <img src="./images/3.png" alt="Indonesia EV insights" class="regional-insight-icon" />
            <div class="regional-insight-content">
              <h4>Indonesia's electric motorbikes hit speed bump over subsidy rollback</h4>
              <a href="https://www.businesstimes.com.sg/international/asean/indonesias-electric-motorcycle-market-hits-speed-bump-following-subsidy-rollback" target="_blank" rel="noopener noreferrer" class="regional-insight-link">READ MORE →</a>
            </div>
          </div>
          
          <div class="regional-insight-item">
            <img src="./images/4.png" alt="Vietnam EV insights" class="regional-insight-icon" />
            <div class="regional-insight-content">
              <h4>'I buy first, you buy later' strategy drives EV growth for Vietnam's local brand</h4>
              <a href="https://www.businesstimes.com.sg/international/asean/vietnams-ev-boom-vinfast-buys-first-mass-adoption-follows" target="_blank" rel="noopener noreferrer" class="regional-insight-link">READ MORE →</a>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Share Section -->
      <div class="share-story-section">
        <div class="share-story-text">SHARE THIS STORY</div>
        <div class="share-story-icons">
          <a href="https://www.linkedin.com/sharing/share-offsite/?url=https%3A%2F%2Fgraphics.businesstimes.com.sg%2Fspecials%2Fev%2Findex.html" rel="noopener noreferrer" target="_blank" aria-label="Share on LinkedIn" class="share-icon linkedin-share">
            <img src="./images/linkedin_green.png" alt="LinkedIn" />
          </a>
          <a href="https://instagram.com" rel="noopener noreferrer" target="_blank" aria-label="Share on Instagram" class="share-icon instagram-share">
            <img src="./images/ig_green.png" alt="Instagram" />
          </a>
          <a href="https://x.com/intent/tweet?url=https%3A%2F%2Fgraphics.businesstimes.com.sg%2Fspecials%2Fev%2Findex.html&text=How%20will%20the%20EV%20journey%20in%20South-east%20Asia%20unfold%3F" rel="noopener noreferrer" target="_blank" aria-label="Share on Twitter" class="share-icon twitter-share">
            <img src="./images/x_green.png" alt="Twitter" />
          </a>
          <a href="https://www.facebook.com/sharer/sharer.php?u=https%3A%2F%2Fgraphics.businesstimes.com.sg%2Fspecials%2Fev%2Findex.html" rel="noopener noreferrer" target="_blank" aria-label="Share on Facebook" class="share-icon facebook-share">
            <img src="./images/fb_green.png" alt="Facebook" />
          </a>
        </div>
      </div>
    </div>
  </div>
  
  <!-- Credits and Sources Section -->
  <div class="credits-sources-section">
    <p class="credits-text">Produced by: Mario Monreal, Jamille Tran, Goh Ruoxue, Lionel Lim, Kai Teo.</p>
    <p class="sources-text">Sources: The Jakarta Post, PwC, Bank of America, Department of Trade and Industry, Singapore Land Transport Authority, Thailand Board of Investment, Vietnam Ministry of Transport.</p>
  </div>
</div>

<style>
  @import url('https://fonts.googleapis.com/css2?family=Libre+Caslon+Text:ital,wght@0,400;0,700;1,400&family=Public+Sans:wght@300;400;500;600;700&family=Poppins:wght@400;500;600;700&display=swap');

  :global(body) {
    margin: 0;
    padding: 0;
    overflow-x: hidden;
    font-family: 'Libre Caslon Text', serif;
    background-color: #EFF8F3;
    color: #fff;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  :global(h1),
  :global(h2),
  :global(h3),
  :global(h4),
  :global(h5),
  :global(h6) {
    font-family: 'Libre Caslon Text', serif;
  }


  #container {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    z-index: 1;
  }

  .scroll-sections {
    position: relative;
    width: 100%;
    z-index: 2; /* Above the fixed video */
    will-change: scroll-position;
    transform: translateZ(0); /* Force GPU acceleration */
  }

  .panel {
    position: relative;
    width: 100%;
    height: 100vh; /* Each panel takes up full viewport height */
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
  }

  .panel_cards {
    position: relative;
    width: 100%;
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
  }

  .content-box {
    background-color: rgba(255, 255, 255, 0.9);
    padding: 0.3rem 1.5rem;
    border-radius: 8px;
    max-width: 500px;
    opacity: 1; /* Changed from 0 to 1 to make boxes visible by default */
    pointer-events: auto; /* Change from none to auto */
    color: #000;
    position: relative;
    z-index: 3; /* Above the fixed video */
    font-family: Lucida, ui-serif, Georgia, Cambria, 'Times New Roman', Times, serif;
  }
  

  #panel5_3 .content-box,
  #panel5_4 .content-box,
  #panel5_5 .content-box {
    font-family: 'Poppins', sans-serif;
  }

  .content-box:not(.article-header-overlay) {
    background-color: rgba(255, 255, 255, 0.84);
    border-radius: 0px;
    width: 400px;
    max-width: 463px;
    border: 1px solid #a3a3a3;
  }

  #panel20 .content-box{
    max-width: 2000px !important;
    padding-left: 10px;
    padding-right: 10px;
  }

  #panel20 .videos-wrapper {
    margin: 0 auto 10px auto;
  }

  #panel16_1_1 .content-box{
    background-color: rgba(0, 0, 0, 0);
    padding: 0.3rem 1.5rem;
    border-radius: 8px;
    max-width: 500px;
    opacity: 1; /* Changed from 0 to 1 to make boxes visible by default */
    margin: 0 1rem;
    pointer-events: auto; /* Change from none to auto */
  }

  .content-box h2 {
    margin-top: 0.2rem;
    margin-bottom: 0.5rem;
    color: #000;
    font-family: 'Libre Caslon Text', serif;
  }

  .content-box p {
    line-height: 1.6;
    font-family: 'Libre Caslon Text', serif;
  }

  .title-text {
    max-width: 70%; /* Adjust this value as needed */
    margin: 0 auto; /* This centers the paragraph if it's narrower than its container */
    text-align: center;
  }

  /* Add responsive styles */
  @media (max-width: 768px) {
    .content-box {
      width: auto;
      max-width: 90%;
      padding: 0.1rem 1.5rem;

    }
  }

  @media (min-width: 768px) {
      .content-box{
        margin: 0 0 0 calc(2rem + 50px);
      }
  }

  #panel1 {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 10px; /* Space between elements */
    z-index: 2; /* Above video section */
    pointer-events: none; /* Allow clicks to pass through to header */
  }

  #panel1 h2 {
    margin-bottom: 5px;
  }

  #panel1 p {

    font-weight: 600;
    margin: 5px 0; /* Remove default paragraph margins */
    text-rendering: optimizelegibility;
  }

  /* Video Grid Styles */
  .videos-wrapper {
    position: relative;
    width: 100%;
    max-width: 2000px;
    margin: 10px auto;
  }

  .video-grid {
    display: grid;
    grid-template-columns: repeat(2, 600px); /* Two columns with fixed 600px width */
    gap: 20px; /* Increased gap between videos */
    justify-content: center; /* Center the grid */
    margin: 0;
    padding: 0;
  }

  .video-grid-item {
    position: relative;
  }

  .video-container {
    position: relative;
    width: 600px;
    height: 600px; /* Fixed height */
    margin: 0;
    overflow: hidden;
    background-color: #000;
    cursor: pointer;
    padding-bottom: 0; /* Remove the percentage padding */
  }

  .video-element {
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    cursor: pointer; /* Add pointer cursor to indicate clickability */
    z-index: 2; /* Ensure video is above other elements */
  }

  .video-label-container {
    position: absolute;
    top: 10px;
    left: 10px;
    color: white;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
  }

  .video-label {
    margin: 2px 0;
    font-size: 12px;
  }

  .video-label-title {
    font-weight: bold;
    font-size: 14px;
  }

  .video-play-button {
    display: none;
  }

  .video-controls {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    margin-top: 10px;
    gap: 20px;
  }

  .global-play-button,
  .global-mute-button {
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 5px;
  }

  .play-button-icon {
    position: relative;
    width: 40px;
    height: 40px;
  }

  .play-circle {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 2px solid white;
    border-radius: 50%;
  }

  .play-triangle {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-40%, -50%);
    width: 20px;
    height: 20px;
  }

  .mute-icon {
    font-size: 25px;
    color: white;
  }

  .video-captions-overlay {
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%); /* Center the element horizontally */
    max-width: 80%; /* Maximum width as a percentage of the parent */
    width: auto; /* Change from fixed 500px to auto */
    min-width: 200px; /* Add minimum width */
    background-color: rgba(0, 0, 0, 0.7);
    color: white;
    padding: 10px 20px; /* Increased horizontal padding */
    text-align: center;
    z-index: 10;
    /* border-radius: 4px 4px 0 0; */
    display: inline-block; /* Makes the element only as wide as its content */
  }

  #video-caption-text, #video-caption-text-motion {
    margin: 0;
    font-size: 16px;
    white-space: normal; /* Changed from nowrap to normal to allow wrapping */
    max-width: 100%; /* Ensure text stays within container */
    overflow-wrap: break-word; /* Help with breaking long words if needed */
    word-wrap: break-word; /* For older browsers */
    max-height: 2.4em; /* Limit to approximately 2 lines */
    overflow: hidden; /* Hide any text beyond 2 lines */
    line-height: 1.2; /* Adjust line height for better readability */
  }

  /* Tablet Breakpoint */
  @media (max-width: 1200px) {
    .video-grid {
      grid-template-columns: repeat(2, 450px); /* Smaller width for tablets */
      gap: 15px;
    }
    
    .video-container {
      width: 450px;
      height: 450px;
    }
    
    .video-captions-overlay {
      max-width: 90%;
    }
    
    #video-caption-text, #video-caption-text-motion {
      font-size: 14px;
    }

    #bills-grid {
      grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
      gap: 12px;
      padding: 15px;
    }
    
    #featured-bill-floating {
      width: 600px;
    }
    
    .single-bill {
      width: 130px;
    }
  }

  /* Small Tablet Breakpoint */
  @media (max-width: 950px) {
    .video-grid {
      grid-template-columns: repeat(2, 350px);
      gap: 10px;
    }
    
    .video-container {
      width: 350px;
      height: 350px;
    }
  }

  /* Mobile Breakpoint */
  @media (max-width: 767px) {
    .video-grid {
      grid-template-columns: repeat(1, 85vw) !important; /* Single column for mobile */
      gap: 15px;
    }
    
    .video-container {
      width: 85vw !important;
      height: 85vw !important; /* Square aspect ratio */
    }
    
    .video-label-container {
      top: 5px;
      left: 5px;
    }
    
    .video-label {
      font-size: 10px;
    }
    
    .video-label-title {
      font-size: 12px;
    }
    
    .video-captions-overlay {
      max-width: 90%; 
      padding: 8px 15px;
    }
    
    #video-caption-text, #video-caption-text-motion {
      font-size: 12px;
      max-height: 2.6em; 
    }
    
    .play-button-icon {
      width: 30px;
      height: 30px;
    }
    
    .play-triangle {
      width: 15px;
      height: 15px;
    }
    
    .mute-icon {
      font-size: 20px;
    }

    #bills-grid {
      grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
      gap: 10px;
      padding: 10px;
      max-width: 95%;
    }
    
    #featured-bill-floating {
      width: 90%;
      max-width: 400px;
    }
    
    .single-bill {
      width: 100px;
    }
    
    .bill-counter {
      top: 10%;
      right: 5%;
      font-size: 1.8rem;
    }
    
    .bill-counter .count {
      font-size: 3rem;
    }
    
    .full-screen-title,
    .full-screen-subtitle {
      width: 90%;
      font-size: 14px;
    }

    .byline {
      font-size: 16px !important;
      font-family: 'Poppins', sans-serif;
      font-weight: 400;
      font-style: italic;
      text-transform: uppercase;
    }
    
    /* Adjust panel1 for mobile */
    #panel1 {
      align-items: flex-start;
      justify-content: center;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100vh;
      z-index: 2;
    }
  }

  @media (min-width: 768px) {
    .byline {
      font-size: 16px !important;
      font-family: 'Poppins', sans-serif;
      font-weight: 400 !important;
      margin-top: 5px; 
      text-transform: uppercase;
    }
    
    /* Adjust panel1 for desktop */
    #panel1 {
      align-items: flex-start;
      justify-content: center;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100vh;
      z-index: 2;
    }
  }

  /* Small Mobile Breakpoint */
  @media (max-width: 480px) {
    .content-box {

    }
    
    .video-grid {
      grid-template-columns: repeat(1, 60vw);
      gap: 10px;
    }
    
    .video-container {
      width: 60vw;
      height: 60vw;
    }
    
    .video-controls {
      gap: 10px;
    }

    #bills-grid {
      grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
      gap: 6px;
      padding: 8px;
    }
    
    #featured-bill-floating {
      max-width: 300px;
    }
    
    .single-bill {
      width: 60px;
    }
    
    .bill-counter {
      font-size: 1.5rem;
    }
    
    .bill-counter .count {
      font-size: 2.5rem;
    }
  }

  #panel4 .content-box {
    max-width: 2000px;
    padding-left: 10px;
    padding-right: 10px;
  }

  /* Make content box for panel15 wider */
  #panel15 .content-box {
    max-width: 2000px;
    padding-left: 10px;
    padding-right: 10px;
  }

  /* Policy, Market Strengths, and Market Weaknesses Visualization Content Boxes */
  #panel5_3 .content-box,
  #panel5_4 .content-box,
  #panel5_5 .content-box {
    background-color: transparent !important;
    border: none !important;
    max-width: 1200px !important;
    width: 100% !important;
    padding-left: 10px;
    padding-right: 10px;
    margin: 0 auto !important; /* Center the content boxes */
    overflow: hidden; /* Prevent overflow */
  }

  /* Center the policy visualization panels */
  #panel5_3,
  #panel5_4,
  #panel5_5 {
    justify-content: center !important;
  }

  /* Add spacing between market strengths and weaknesses sections */
  #panel5_5 {
    margin-top: 3rem;
  }


  #panel5_3 .content-box h2,
  #panel5_4 .content-box h2,
  #panel5_5 .content-box h2 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #2c3e50;
    margin: 2rem 0 1rem 0;
    font-family: 'Poppins', sans-serif;
  }

  /* Full-screen bills animation styles */
  .bills-fullscreen-container {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    z-index: 10;
  }

  .full-screen-title {
    position: absolute;
    top: 15%;
    left: 50%;
    transform: translateX(-50%);
    font-size: 16px;
    color: white;
    text-align: left;
    z-index: 11;
    width: 80%;
    opacity: 0; /* Start hidden */
  }

  .full-screen-subtitle {
    position: absolute;
    top: 25%;
    left: 50%;
    transform: translateX(-50%);
    font-size: 16px;
    color: white;
    text-align: left;
    z-index: 11;
    width: 80%;
    opacity: 0; /* Start hidden */
  }

  /* Add this to your CSS file */
  .bill-counter {
    position: fixed;
    top: 20%;
    right: 10%;
    font-size: 2.5rem;
    font-weight: bold;
    color: white;
    text-shadow: 0 0 10px rgba(0,0,0,0.5);
    z-index: 100;
  }

  .bill-counter .count {
    font-size: 4rem;
    color: #ffcc00;
  }

  /* Bills Grid Styles */
  #bills-grid {
    width: 100%;
    max-width: 1200px;
    position: relative;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 15px;
    padding: 20px;
    margin: 0 auto;
  }

  #featured-bill-floating {
    position: absolute;
    top: 20%;
    left: 50%;
    transform: translateX(-50%);
    width: 800px;
    z-index: 100;
  }

  .grid-bill {
    position: relative;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 5px rgba(0,0,0,0.2);
    transition: transform 0.3s ease;
    opacity: 0;
    transform: scale(0.8);
    background-color: #fff;
  }

  .bill-thumbnail {
    width: 100%;
    height: auto;
    display: block;
  }

  #panel1 {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 10px; /* Space between elements */
    z-index: 2; /* Above video section */
  }

  #panel1 h2 {
    margin-bottom: 5px;
  }

  #panel1 p {
    font-weight: 600;
    margin: 5px 0; /* Remove default paragraph margins */
    text-rendering: optimizelegibility;
  }

  .animation-test {
    margin-top: 2rem;
    display: flex;
    justify-content: center;
    align-items: flex-start;
  }

  /* Make the bills panel taller to accommodate all bills */
  #panel16_1 {
    min-height: 100vh;
    height: auto;
    padding: 50px 0;
  }

  /* Hide content boxes in bills panels */
  #single-bill-container {
    position: fixed;
    z-index: 100;
  }

  .single-bill {
    width: 150px;
    height: auto;
    box-shadow: 0 5px 15px rgba(0,0,0,0.3);
    background-color: white;
    border-radius: 8px;
    overflow: hidden;
  }

  .single-bill img {
    width: 100%;
    height: auto;
    display: block;
  }

  /* Highlight effect for when a mesh is being highlighted */
  @keyframes pulse {
    0% { opacity: 0.4; }
    50% { opacity: 0.8; }
    100% { opacity: 0.4; }
  }

  /* Style for the panel that has active highlights */
  .panel.highlight-active {
    /* Optional: add a subtle indicator that this panel has active highlights */
    border-left: 3px solid #ff3333;
  }

  /* Make sure content boxes are more visible when their panel has highlights */
  .panel.highlight-active .content-box {
    background-color: rgba(0, 0, 0, 0.85);
    box-shadow: 0 0 20px rgba(255, 51, 51, 0.2);
  }

  /* Video control icons */
  .video-control-icon {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    cursor: pointer;
    z-index: 10;
    transition: background-color 0.3s ease;
  }

  .video-control-icon:hover {
    background-color: rgba(0, 0, 0, 0.7);
  }

  .play-icon, .pause-icon {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    width: 100%;
    height: 100%;
  }

  .play-icon svg, .pause-icon svg {
    width: 36px;
    height: 36px;
    filter: drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.5));
  }

  /* Make sure the video container has position relative for proper icon positioning */
  .video-container {
    position: relative;
  }

  /* Article Content Styling */
  .article-content {
    position: relative;
    z-index: 2;
    padding: 2rem 2rem;
    max-width: 800px;
    margin: 0 auto;
    line-height: 1.7;
    font-size: 1.1rem;
    color: #333;
    font-family: Lucida, ui-serif, Georgia, Cambria, 'Times New Roman', Times, serif;
  }

  /* Flourish Embed Styling */
  .flourish-embed {
    position: relative;
    z-index: 2;
    max-width: 800px;
    margin: 0 0 40px 0;
    width: 100%;

  }

  .flourish-embed iframe {
    width: 100%;
    height: 100%;
    border: none;
  }



  .article-text {
    max-width: 100%;
    font-family: Lucida, ui-serif, Georgia, Cambria, 'Times New Roman', Times, serif;
  }

  .article-text p {
    margin-bottom: 1.5rem;
    line-height: 1.7;
    font-family: Lucida, ui-serif, Georgia, Cambria, 'Times New Roman', Times, serif;
  }

  .article-text h3 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #2c3e50;
    margin: 2rem 0 1rem 0;
    font-family: 'Libre Caslon Text', serif;
    line-height: 1.2;
  }

  .article-content a,
  .article-text a {
    color: inherit;
    text-decoration: underline;
  }

  .article-content a:hover,
  .article-text a:hover {
    color: inherit;
  }

  /* Regional Insights Section */
  .regional-insights-section {
    margin: 4rem 0;
    padding: 2rem;
    border: 1px solid #26985c;
    border-radius: 4px;
    max-width: 100%;
  }

  .regional-insights-section h3 {
    font-size: 1.5rem;
    font-weight: 400;
    color: #2c3e50;
    margin: 0 0 2rem 0;
    font-family: 'Poppins', sans-serif;
  }

  .regional-insights-container {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .regional-insight-item {
    display: flex;
    align-items: flex-start;
    gap: 1.5rem;
    padding: 1rem 0;
    border-bottom: 1px solid #e0e0e0;
  }

  .regional-insight-item:last-child {
    border-bottom: none;
  }

  .regional-insight-icon {
    width: 120px;
    height: 120px;
    object-fit: contain;
    flex-shrink: 0;
    filter: brightness(0.9) saturate(1.2);
  }

  .regional-insight-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .regional-insight-content h4 {
    font-size: 1.1rem;
    font-weight: 500;
    color: #2c3e50;
    margin: 0;
    line-height: 1.4;
    font-family: 'Poppins', sans-serif;
  }

  .regional-insight-link {
    font-size: 12px;
    font-weight: 400;
    color: #2c2c2c;
    text-decoration: none;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: color 0.3s ease;
    font-family: 'Poppins', sans-serif;
  }

  .regional-insight-link:hover {
    color: #004499;
    text-decoration: underline;
  }

  /* Responsive regional insights */
  @media (max-width: 768px) {
    .regional-insights-section {
      padding: 1.5rem;
      margin: 3rem 0;
    }

    .regional-insights-section h3 {
      font-size: 1.3rem;
      margin-bottom: 1.5rem;
    }

    .regional-insight-item {
      flex-direction: column;
      gap: 1rem;
      align-items: center;
      text-align: center;
    }

    .regional-insight-icon {
      width: 100px;
      height: 100px;
    }

    .regional-insight-content {
      align-items: center;
    }

    .regional-insight-content h4 {
      font-size: 1rem;
    }

    .regional-insight-link {
      font-size: 0.8rem;
    }
  }

  @media (max-width: 480px) {
    .regional-insights-section {
      padding: 1rem;
      margin: 2rem 0;
    }

    .regional-insights-section h3 {
      font-size: 1.2rem;
    }

    .regional-insight-icon {
      width: 80px;
      height: 80px;
    }

    .regional-insight-content h4 {
      font-size: 0.9rem;
    }

    .regional-insight-link {
      font-size: 0.75rem;
    }
  }

  /* Share Story Section */
  .share-story-section {
    margin: 4rem 0 2rem 0;
    padding: 2rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
  }

  .share-story-text {
    font-family: 'Poppins', sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #000;
    text-align: center;
    letter-spacing: 0.5px;
  }

  .share-story-icons {
    display: flex;
    gap: 15px;
    align-items: center;
    justify-content: center;
  }

  .share-story-icons .share-icon {
    display: block;
    width: 32px;
    height: 32px;
    transition: opacity 0.3s ease;
  }

  .share-story-icons .share-icon:hover {
    opacity: 0.7;
  }

  .share-story-icons .share-icon img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  /* Credits and Sources Section */
  .credits-sources-section {
    margin: 3rem auto 2rem auto;
    padding: 2rem;
    border-top: 1px solid #e0e0e0;
    font-family: 'Poppins', sans-serif;
    max-width: 800px;
    width: 100%;
    box-sizing: border-box;
  }

  .credits-text,
  .sources-text {
    font-family: 'Poppins', sans-serif;
    font-size: 0.9rem;
    font-weight: 400;
    color: #666;
    line-height: 1.6;
    margin: 0.5rem 0;
  }

  /* Responsive share story section */
  @media (max-width: 768px) {
    .share-story-section {
      margin: 3rem 0 1.5rem 0;
      padding: 1.5rem;
      gap: 1rem;
    }

    .share-story-text {
      font-size: 12px;
    }

    .share-story-icons {
      gap: 12px;
    }

    .share-story-icons .share-icon {
      width: 28px;
      height: 28px;
    }

    .credits-sources-section {
      padding: 2rem 1rem;
    }

    .credits-text,
    .sources-text {
      font-size: 1rem;
    }
  }

  /* Responsive article styling */
  @media (max-width: 768px) {
    .article-content {
      padding: 1rem 1rem;
      font-size: 1rem;
    }
    
    .article-text h3 {
      font-size: 1.3rem;
    }

    /* Responsive styles for policy, market strengths, and market weaknesses titles */
    #panel5_3 .content-box h2,
    #panel5_4 .content-box h2,
    #panel5_5 .content-box h2 {
      font-size: 1.3rem;
    }
  }

  /* Policy Visualization Styling */
  .policy-visualization {
    margin-top: 1rem;
    width: 100%;
    max-width: 1200px;
    margin-left: auto;
    margin-right: auto;
  }

  /* Allow market strengths and weaknesses cards to break out of article-content constraint on desktop */
  @media (min-width: 1029px) {
    .article-content #panel5_3,
    .article-content #panel5_4,
    .article-content #panel5_5 {
      width: 100vw;
      max-width: 100vw;
      margin-left: calc(-50vw + 50%);
      position: relative;
      left: 0;
    }
    
    .article-content #panel5_3 .content-box,
    .article-content #panel5_4 .content-box,
    .article-content #panel5_5 .content-box {
      max-width: 1200px !important;
      width: 100% !important;
      margin-left: auto !important;
      margin-right: auto !important;
    }
  }

  .policy-scroll-container {
    position: relative;
    width: 100%;
    max-width: 100%;
    overflow: hidden;
    scrollbar-width: none; /* Firefox - hide scrollbar on desktop */
    -ms-overflow-style: none; /* IE and Edge - hide scrollbar on desktop */
  }

  .policy-scroll-container::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera - hide scrollbar on desktop */
  }

  .policy-grid {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    grid-template-rows: repeat(1, auto);
    gap: 0.5rem;
    margin: 1rem auto 0 auto;
    width: 100%;
    max-width: 1200px;
    justify-content: center;
  }
  
  /* In-app mode: vertical layout instead of horizontal scrolling */
  :global(body.in-app-mode) .policy-scroll-container {
    overflow: visible;
    height: auto;
  }
  
  :global(body.in-app-mode) .policy-grid {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    gap: 1rem;
    width: 100%;
    max-width: 100%;
    margin: 1rem 0 0 0;
    padding: 0;
  }
  
  :global(body.in-app-mode) .country-card {
    width: 100%;
    max-width: 100%;
    min-width: 100%;
    margin: 0;
  }
  
  :global(body.in-app-mode) .indicator-dot {
    display: none !important; /* Hide indicators in app mode */
  }

  .indicator-dot {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    margin-top: 1rem;
  }

  .indicator-dot-step {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.3);
    transition: all 0.3s ease;
    cursor: pointer;
  }

  .indicator-dot-step.active {
    background-color: #000000;
    width: 24px;
    border-radius: 4px;
  }

  .country-card {
    background: #ffffff;
    border-radius: 24px;
    padding: 1.2rem;
    transition: all 0.3s ease;
    position: relative;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    min-height: 320px;
    display: flex;
    flex-direction: column;
  }


  .country-name-new {
    font-size: 1.6rem;
    font-weight: 400;
    color: #006400;
    margin-bottom: 1rem;
    font-family: 'Poppins', sans-serif;
    text-align: center;
  }

  .country-name {
    font-size: 1.2rem;
    font-weight: 700;
    color: #333333;
    margin-bottom: 1rem;
    letter-spacing: 0.5px;
    padding-bottom: 0.5rem;
    font-family: 'Libre Caslon Text', serif;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .country-name svg {
    flex-shrink: 0;
    width: 120px;
    height: 20px;
  }

  .country-line-icon {
    flex-shrink: 0;
    width: 120px;
    height: 20px;
    transform-origin: center;
  }

  .section-heading {
    font-size: 0.85rem;
    font-weight: 500;
    color: #006400;
    text-align: center;
    margin: 1rem 0 0.75rem 0;
    font-family: 'Poppins', sans-serif;
    letter-spacing: 0.5px;
  }

  .adoption-section > .section-heading:first-child {
    margin-top: 0.25rem;
  }

  .adoption-section {
    margin-bottom: 0.75rem;
  }

  .adoption-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 1rem;
    text-align: center;
  }

  .ev-icon {
    width: 40px;
    height: 20px;
    margin: 0 auto 0.4rem auto;
    object-fit: contain;
  }

  .stat-number {
    font-size: 1.8rem;
    font-weight: 400;
    color: #000000;
    line-height: 1.4;
    font-family: 'Poppins', sans-serif;
    text-align: center;
  }

  .stat-description {
    font-size: 0.9rem;
    color: #000000;
    line-height: 1.4;
    font-family: 'Poppins', sans-serif;
    text-align: center;
  }

  .production-section {
    margin-top: 0.25rem;
  }

  .production-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .policy-target {
    margin-bottom: 1rem;
  }

  .target-with-bar {
    margin-bottom: 0.5rem;
  }

  .bar-container {
    margin-bottom: 0.8rem;
  }

  .bar-label {
    font-size: 0.9rem;
    font-weight: 600;
    color: #555555;
    margin-bottom: 0.3rem;
  }

  .bar {
    position: relative;
    height: 24px;
    background-color: #f0f0f0;
    border: 1px solid #cccccc;
    border-radius: 4px;
    overflow: hidden;
  }

  .bar-fill {
    height: 100%;
    background-color: #333333;
    transition: width 0.3s ease;
  }

  .bar-value {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 0.8rem;
    font-weight: 700;
    color: #ffffff;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  }

  .target-text {
    font-size: 0.9rem;
    color: #666666;
    font-style: italic;
    margin-top: 0.5rem;
  }

  .policy-detail {
    margin-top: 1rem;
  }

  .detail-with-icon {
    font-size: 0.9rem;
    color: #666666;
    line-height: 1.4;
    padding: 0.8rem;
    background-color: #f8f8f8;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .detail-icon {
    width: 16px;
    height: 16px;
    color: #333333;
    flex-shrink: 0;
  }

  /* Article Header Overlay Styles */
  .article-header-overlay {
    background-color: transparent !important;
    text-align: left;
    max-width: 800px;
    padding: 2rem 1.5rem;
    pointer-events: auto; /* Re-enable clicks on the content itself */
  }

  .article-header-overlay .article-title {
    font-size: 64px;
    font-weight: 400;
    color:rgb(0, 0, 0);
    margin: 5px 0;
    text-align: left;
    font-family: 'Libre Caslon Text', serif;
    line-height: 1.2;
  }

  .article-header-overlay .article-subtitle {
    font-size: 1.4rem;
    font-weight: 400;
    color: #ffffff;
    margin: 0 0 2rem 0;
    line-height: 1.5;
    opacity: 0.95;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
  }

  .article-header-overlay .article-byline {
    font-size: 16px;
    font-family: 'Poppins', sans-serif;
    font-weight: 400 !important;
    color: #000;
    text-align: left;
  }

  .article-header-overlay .article-byline a {
    color: #000;
    text-decoration: underline;
  }

  .article-header-overlay .article-published {
    font-size: 14px;
    font-family: 'Poppins', sans-serif;
    font-weight: 400! important;
    color: #000;
    text-align: left;
    margin-top: 0.5rem;
  }

  /* Responsive article header overlay */
  @media (max-width: 768px) {
    .article-header-overlay {
      padding: 1.5rem 1rem;
    }
    
    .article-header-overlay .article-title {
      font-size: 3.5rem;
      margin-bottom: 1rem;
    }
    
    .article-header-overlay .article-subtitle {
      font-size: 1.1rem;
      margin-bottom: 1.5rem;
    }
    
    .article-header-overlay .article-byline {
      font-size: 16px;
      font-family: 'Poppins', sans-serif;
      font-weight: 400 !important;
    }
  }

  @media (max-width: 480px) {
    .article-header-overlay .article-title {
      font-size: 30px;
      text-align: center;
    }
    
    .article-header-overlay .article-byline {
      text-align: center;
    }
    
    .article-header-overlay .article-published {
      text-align: center;
    }
    
    .article-header-overlay .article-subtitle {
      font-size: 1rem;
    }
  }

  /* Video Section Styles - positioned directly below header */
  .video-section {
    margin: 0; /* No margin - start directly below header */
    padding: 0; /* No padding */
    width: 100%;
    position: sticky;
    top: 0;
    z-index: 1; /* Ensure it's below header but above background */
    height: 100vh;
    background-color: #000;
  }
  
  /* Intro Video Styles */
  .intro-video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 3; /* Above sequence when playing */
  }

  /* Sequence container - always rendered but hidden behind video */
  .sequence-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 2; /* Behind video when video is playing */
  }


  .video-section h3 {
    color: #fff;
    margin-bottom: 1rem;
    text-align: center;
    font-family: 'Libre Caslon Text', serif;
  }

  .video-section p {
    color: #fff;
    text-align: center;
    margin-top: 1rem;
    font-size: 14px;
    opacity: 0.8;
  }

  /* Country selector with hamburger styling */
  .country-selector {
    position: relative;
    display: none; /* Hidden on desktop */
    margin-bottom: 1rem;
    z-index: 10;
  }

  .hamburger-button {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
    padding: 0.75rem 1rem;
    background-color: #ffffff;
    border: none;
    border-radius: 24px;
    cursor: pointer;
    font-family: 'Poppins', sans-serif;
    font-size: 1rem;
    color: #006400;
    transition: background-color 0.2s ease;
  }

  .hamburger-button:hover {
    background-color: #f5f5f5;
  }

  .hamburger-button:active {
    transform: scale(0.98);
  }

  .hamburger-button svg {
    flex-shrink: 0;
    stroke: #006400;
  }

  .selected-country {
    flex: 1;
    text-align: left;
    font-weight: 500;
  }

  .tap-hint {
    font-size: 0.85rem;
    color: #666666;
    font-weight: 400;
    opacity: 0.8;
    font-style: italic;
  }

  .country-menu {
    position: absolute;
    top: calc(100% + 0.5rem);
    left: 0;
    right: 0;
    background-color: #ffffff;
    border-radius: 24px;
    overflow: hidden;
    z-index: 100;
    animation: slideDown 0.2s ease;
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .country-menu-item {
    display: block;
    width: 100%;
    padding: 0.875rem 1rem;
    background: none;
    border: none;
    text-align: left;
    font-family: 'Poppins', sans-serif;
    font-size: 1rem;
    color: #000000;
    cursor: pointer;
    transition: background-color 0.2s ease;
    border-bottom: 1px solid #f0f0f0;
  }

  .country-menu-item:last-child {
    border-bottom: none;
  }

  .country-menu-item:hover {
    background-color: #f5f5f5;
  }

  .country-menu-item.active {
    background-color: #e8f5e8;
    color: #006400;
    font-weight: 600;
  }

  /* Hide cards with mobile-hidden class on mobile */
  @media (max-width: 1028px) {
    .country-card.mobile-hidden {
      display: none !important;
    }
  }

  /* Show all cards on desktop */
  @media (min-width: 1029px) {
    .country-card.mobile-hidden {
      display: flex !important;
    }
  }

  /* Responsive policy grid */
  @media (max-width: 1024px) {
  }

  @media (max-width: 1028px) {
    /* Show country selector on mobile */
    .country-selector {
      display: block;
      padding: 0 1rem;
      box-sizing: border-box;
    }
    #panel5_3 .content-box,
    #panel5_4 .content-box,
    #panel5_5 .content-box {
      overflow: hidden;
      width: 100% !important;
      max-width: 100%;
    }

    .policy-visualization {
      width: 100%;
      max-width: 100%;
      overflow: hidden;
    }

    .policy-scroll-container {
      overflow: visible;
      overflow-x: visible;
      overflow-y: visible;
      width: 100%;
      max-width: 100%;
      position: relative;
      height: auto;
      padding-bottom: 0;
    }

    .policy-grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: 1rem;
      width: 100%;
      max-width: 100%;
      min-width: 100%;
      margin: 1rem 0 0 0;
      padding: 0 1rem;
      box-sizing: border-box;
    }
    
    /* Ensure hidden cards don't take up space */
    .policy-grid .country-card.mobile-hidden {
      display: none !important;
    }
    
    .country-card {
      padding: 1rem;
      width: 100%;
      min-width: 100%;
      max-width: 100%;
      box-sizing: border-box;
      min-height: auto;
      height: auto;
    }
    
    .indicator-dot {
      display: none !important;
    }
    
    /* In-app mode: override mobile styles to use vertical layout */
    :global(body.in-app-mode) .policy-scroll-container {
      overflow: visible !important;
      overflow-x: visible !important;
      overflow-y: visible !important;
      height: auto !important;
      padding-bottom: 0 !important;
      touch-action: auto !important;
    }
    
    :global(body.in-app-mode) .policy-grid {
      display: grid !important;
      grid-template-columns: 1fr !important;
      flex-direction: column !important;
      width: 100% !important;
      max-width: 100% !important;
      min-width: 100% !important;
      margin: 1rem 0 0 0 !important;
      padding: 0 1rem !important;
    }
    
    :global(body.in-app-mode) .country-card {
      width: 100% !important;
      min-width: 100% !important;
      max-width: 100% !important;
      padding: 1rem !important;
    }
    
    :global(body.in-app-mode) .indicator-dot {
      display: none !important;
    }
    
    .country-name {
      font-size: 1.1rem;
    }
    
    .bar-label {
      font-size: 0.8rem;
    }
    
    .bar {
      height: 20px;
    }
    
    .bar-value {
      font-size: 0.7rem;
    }
    
    .target-text {
      font-size: 0.8rem;
    }
    
    .detail-with-icon {
      font-size: 0.8rem;
      padding: 0.6rem;
    }
  }


  /* Market Strengths Visualization Styling */
  .market-strengths-visualization {
    margin-top: 2rem;
  }

  /* Market Weaknesses Visualization Styling */
  .market-weaknesses-visualization {
    margin-top: 2rem;
  }

  .weakness-content {
    margin-top: 1rem;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  }

  .weakness-summary {
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    flex-grow: 1;
  }

  .weakness-main {
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    padding: 0.6rem;
    background-color: #FEE9DA;
    border: 1px solid #FABD96;
    border-radius: 4px;
  }

  .weakness-icon {
    width: 18px;
    height: 18px;
    color: #ec1846;
    flex-shrink: 0;
    margin-top: 0.1rem;
  }

  .weakness-text {
    flex-grow: 1;
  }

  .weakness-title {
    font-size: 0.85rem;
    font-weight: 600;
    color: #ec1846;
    margin-bottom: 0.2rem;
    line-height: 1.2;
  }

  .weakness-description {
    font-size: 16px;
    color: #ec1846;
    line-height: 1.3;
  }

  .weakness-additional {
    margin-top: 0.5rem;
  }

  .weakness-detail {
    font-size: 16px;
    color: #EC1846;
    line-height: 1.3;
    padding: 0.4rem 0.6rem;
    background-color: #FEE9DA;
    border: 1px solid #FABD96;
    border-radius: 4px;
    display: flex;
    align-items: flex-start;
    gap: 0.4rem;
    margin-bottom: 0.4rem;
  }

  .weakness-detail .detail-icon {
    width: 16px;
    height: 16px;
    color: #EC1846;
    flex-shrink: 0;
  }

  .strength-content {
    margin-top: 1rem;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  }

  .strength-summary {
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    flex-grow: 1;
  }

  .strength-main {
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    padding: 0.6rem;
    background-color: #f0f9f0;
    border: 1px solid #4caf50;
    border-radius: 4px;
  }

  .strength-icon {
    width: 18px;
    height: 18px;
    color: #2e7d32;
    flex-shrink: 0;
    margin-top: 0.1rem;
  }

  .strength-text {
    flex-grow: 1;
  }

  .strength-title {
    font-size: 0.85rem;
    font-weight: 600;
    color: #1b5e20;
    margin-bottom: 0.2rem;
    line-height: 1.2;
  }

  .strength-description {
    font-size: 16px;
    color: #2e7d32;
    line-height: 1.3;
  }

  .strength-additional {
    margin-top: 0.5rem;
  }

  .strength-detail {
    font-size: 16px;
    color: #2e7d32;
    line-height: 1.3;
    padding: 0.4rem 0.6rem;
    background-color: #e8f5e8;
    border: 1px solid #66bb6a;
    border-radius: 4px;
    display: flex;
    align-items: flex-start;
    gap: 0.4rem;
    margin-bottom: 0.4rem;
  }

  .strength-detail .detail-icon {
    width: 16px;
    height: 16px;
    color: #2e7d32;
    flex-shrink: 0;
  }

  /* Responsive market strengths and weaknesses styling */
  @media (max-width: 1024px) {
  }

  @media (max-width: 768px) {
    
    .strength-main,
    .weakness-main {
      padding: 0.5rem;
      gap: 0.5rem;
    }
    
    .strength-icon,
    .weakness-icon {
      width: 16px;
      height: 16px;
    }
    
    .strength-title,
    .weakness-title {
      font-size: 0.8rem;
    }
    
    .strength-description,
    .weakness-description {
      font-size: 13px;
    }
    
    .strength-detail,
    .weakness-detail {
      font-size: 13px;
      padding: 0.3rem 0.5rem;
    }
    
    .detail-icon {
      width: 12px;
      height: 12px;
    }
  }

  @media (max-width: 480px) {

    
    .strength-main,
    .weakness-main {
      padding: 0.4rem;
      gap: 0.4rem;
    }
    
    .strength-icon,
    .weakness-icon {
      width: 14px;
      height: 14px;
    }
    
    .strength-title,
    .weakness-title {
      font-size: 0.75rem;
    }
    
    .strength-description,
    .weakness-description {
      font-size: 12.5px;
    }
    
    .strength-detail,
    .weakness-detail {
      font-size: 12.5px;
      padding: 0.3rem 0.4rem;
    }
    
    .detail-icon {
      width: 10px;
      height: 10px;
    }
  }
</style>



